export class HomeView {
	constructor(state, options = {}) {
		this.state = state;
		this.options = options;
		this.element = null;
		this._unsubscribe = null;
	}

	render() {
		this.element = document.createElement('div');
		this.element.className = 'liveChat-view liveChat-home-view';

		this._updateContent();

		this._unsubscribe = this.state.subscribe((type) => {
			if (
				type === 'homeChangelogUpdate' ||
				type === 'conversationsUpdate' ||
				type === 'availabilityUpdate' ||
				type === 'settingsUpdate'
			) {
				this._updateContent();
			}
		});

		return this.element;
	}

	_updateContent() {
		const isUnavailable =
			this.state.businessHoursState === 'offline' ||
			this.state.businessHoursState === 'away' ||
			this.state.businessHoursState === 'holiday';
		const avatarsHtml = (this.state.showAvatars && !isUnavailable) ? this._renderAvatarStack() : '';
		const recentChangelogHtml = this._renderRecentChangelog();
		const availabilityHtml = this._renderAvailabilityStatus();

		this.element.innerHTML = `
			<div class="liveChat-home-scroll">
				<div class="liveChat-home-header">
					<div class="liveChat-home-header-top">
						<div class="liveChat-home-logo">
							${this.options.logoUrl ? `<img src="${this.options.logoUrl}" alt="${this.state.teamName}" />` : ''}
						</div>
						<div class="liveChat-home-avatars">${avatarsHtml || availabilityHtml}</div>
					</div>
					<div class="liveChat-home-welcome">
						<span class="liveChat-home-greeting">${this.state.greetingMessage}</span>
						<span class="liveChat-home-question">${this.state.welcomeMessage}</span>
					</div>
				</div>

				<div class="liveChat-home-body">
					${this._renderMessageButton()}
					${this._renderFeaturedCard()}
					${recentChangelogHtml}
				</div>
			</div>
		`;

		this._attachEvents();
	}

	_renderAvatarStack() {
		const avatars = this.state.teamAvatars;
		const colors = ['#5856d6', '#007aff', '#34c759', '#ff9500'];

		if (!avatars || avatars.length === 0) {
			return '';
		}

		const avatarItems = avatars
			.slice(0, 4)
			.map((avatar, index) => {
				if (typeof avatar === 'string' && avatar.startsWith('http')) {
					return `<div class="sdk-avatar sdk-avatar-lg"><img src="${avatar}" alt="Team member" /></div>`;
				}
				return `<div class="sdk-avatar sdk-avatar-lg" style="background: ${colors[index % colors.length]};">${avatar.charAt(0).toUpperCase()}</div>`;
			})
			.join('');

		return `<div class="liveChat-avatar-stack">${avatarItems}</div>`;
	}

	_renderAvailabilityStatus() {
		const businessState = this.state.businessHoursState;

		if (businessState === 'holiday') {
			const name = this.state.holidayName;
			let text = name ? `Closed for ${name}` : "We're closed for a holiday";
			if (this.state.holidayDate) {
				const d = new Date(this.state.holidayDate);
				const dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
				text = name ? `Closed for ${name} (${dateLabel})` : `Closed for the holiday (${dateLabel})`;
			}
			return `
				<div class="liveChat-home-availability">
					<span class="liveChat-availability-dot liveChat-availability-away"></span>
					<span class="liveChat-availability-text">${text}</span>
				</div>
			`;
		}

		if (businessState === 'offline') {
			let offlineText = "We're currently closed";
			if (this.state.holidayName) {
				if (this.state.holidayDate) {
					const d = new Date(this.state.holidayDate);
					const dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
					offlineText = `Closed for ${this.state.holidayName} (${dateLabel})`;
				} else {
					offlineText = `Closed for ${this.state.holidayName}`;
				}
			} else if (this.state.nextOpenAt) {
				const opens = new Date(this.state.nextOpenAt);
				const now = new Date();
				const diffHours = Math.round((opens - now) / 3600000);
				offlineText = diffHours < 24
					? `Opens in ${diffHours}h`
					: `Opens ${opens.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', hour12: true })}`;
			}
			return `
				<div class="liveChat-home-availability">
					<span class="liveChat-availability-dot liveChat-availability-away"></span>
					<span class="liveChat-availability-text">${offlineText}</span>
				</div>
			`;
		}

		if (businessState === 'away') {
			return `
				<div class="liveChat-home-availability">
					<span class="liveChat-availability-dot liveChat-availability-away"></span>
					<span class="liveChat-availability-text">We're currently away</span>
				</div>
			`;
		}

		if (this.state.agentsOnline) {
			return `
				<div class="liveChat-home-availability">
					<span class="liveChat-availability-dot liveChat-availability-online"></span>
					<span class="liveChat-availability-text">${this.state.onlineMessage}</span>
				</div>
			`;
		}

		return `
			<div class="liveChat-home-availability">
				<span class="liveChat-availability-dot liveChat-availability-away"></span>
				<span class="liveChat-availability-text">${this.state.responseTime}</span>
			</div>
		`;
	}

	_renderMessageButton() {
		const openConversation = this.state.conversations.find(
			(c) => c.status === 'open'
		);

		const sendIcon = `<iconify-icon icon="ph:paper-plane-right" width="20" height="20" style="flex-shrink: 0;"></iconify-icon>`;
		const caretIcon = `<iconify-icon icon="ph:caret-right" width="20" height="20" style="flex-shrink: 0;"></iconify-icon>`;

		const isUnavailable =
			this.state.businessHoursState === 'offline' ||
			this.state.businessHoursState === 'away' ||
			this.state.businessHoursState === 'holiday';
		const buttonLabel = isUnavailable ? 'Leave us a message' : this.state.startButtonText;
		const buttonSubtext = isUnavailable
			? "We'll get back to you when we're back"
			: (this.state.responseTime || 'We typically reply within a few minutes');

		const recentCardHtml = openConversation
			? this._renderRecentMessageCard(openConversation)
			: '';

		return `
			${recentCardHtml}
			<button class="liveChat-home-message-btn">
				<div class="liveChat-home-continue-info">
					<span class="liveChat-home-continue-label">${buttonLabel}</span>
					<span class="liveChat-home-message-subtext">${buttonSubtext}</span>
				</div>
				${sendIcon}
			</button>
			<button class="liveChat-home-message-btn liveChat-feedback-btn" data-action="feedback">
				<span>Leave us feedback</span>
				${caretIcon}
			</button>
		`;
	}

	_renderRecentMessageCard(conversation) {
		const logoUrl = this.options.logoUrl;
		const teamName = this.state.teamName || 'Support';
		const logoHtml = logoUrl
			? `<div class="liveChat-home-recent-avatar liveChat-home-recent-avatar-logo"><img src="${logoUrl}" alt="${teamName}" /></div>`
			: `<div class="liveChat-home-recent-avatar" style="background: var(--color-primary);">${teamName.charAt(0).toUpperCase()}</div>`;

		const title = conversation.title || teamName;
		const timeAgo = this._formatTimeAgo(conversation.lastMessageTime);
		const preview = conversation.lastMessage
			? conversation.lastMessage.substring(0, 48) +
				(conversation.lastMessage.length > 48 ? '...' : '')
			: '';
		const hasUnread = conversation.unread > 0;

		return `
			<div class="liveChat-home-recent-card" data-conversation-id="${conversation.id}">
				<div class="liveChat-home-recent-card-label">Recent message</div>
				<div class="liveChat-home-recent-card-row">
					${logoHtml}
					<div class="liveChat-home-recent-card-content">
						<div class="liveChat-home-recent-card-header">
							<span class="liveChat-home-recent-card-name">${title}</span>
							<span class="liveChat-home-recent-card-time">${timeAgo}</span>
						</div>
						<div class="liveChat-home-recent-card-preview">
							<span class="liveChat-home-recent-card-message">${preview}</span>
							${hasUnread ? '<span class="liveChat-home-recent-unread-dot"></span>' : ''}
						</div>
					</div>
				</div>
			</div>
		`;
	}

	_formatTimeAgo(timestamp) {
		if (!timestamp) return '';
		const date = new Date(timestamp);
		const now = new Date();
		const diffMs = now - date;
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);
		if (diffMins < 1) return 'now';
		if (diffMins < 60) return `${diffMins}m`;
		if (diffHours < 24) return `${diffHours}h`;
		if (diffDays < 7) return `${diffDays}d`;
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}

	_renderFeaturedCard() {
		if (!this.options.featuredContent) {
			return '';
		}

		const { title, description, imageUrl, action } =
			this.options.featuredContent;

		return `
			<div class="liveChat-home-featured">
				${imageUrl ? `<img src="${imageUrl}" alt="${title}" class="liveChat-home-featured-image" onerror="this.style.display='none';" />` : ''}
				<div class="liveChat-home-featured-divider"></div>
				<div class="liveChat-home-featured-content">
					<h3>${title}</h3>
					${description ? `<p>${description}</p>` : ''}
					${action ? `<button class="sdk-btn sdk-btn-primary liveChat-home-featured-btn" data-action="${action.type}" data-value="${action.value}">${action.label}</button>` : ''}
				</div>
			</div>
		`;
	}

	_renderRecentChangelog() {
		const changelogItems = this.state.homeChangelogItems;
		if (changelogItems.length === 0) {
			return '';
		}

		const changelogHtml = changelogItems
			.map(
				(item) => `
			<div class="liveChat-home-changelog-card" data-changelog-id="${item.id}">
				${
					item.coverImage
						? `
					<div class="liveChat-home-changelog-cover">
						<img src="${item.coverImage}" alt="${item.title}" onerror="this.style.display='none';" />
						${item.coverText ? `<span class="liveChat-home-changelog-cover-text">${item.coverText}</span>` : ''}
					</div>
				`
						: ''
				}
				<div class="liveChat-home-changelog-card-content">
					<h4 class="liveChat-home-changelog-card-title">${item.title}</h4>
					<p class="liveChat-home-changelog-card-desc">${this._stripHtml(item.description)}</p>
				</div>
			</div>
		`
			)
			.join('');

		return `
			<div class="liveChat-home-changelog-section">
				${changelogHtml}
			</div>
		`;
	}

	_stripHtml(html) {
		if (!html) return '';
		const tmp = document.createElement('div');
		tmp.innerHTML = html;
		return (tmp.textContent || tmp.innerText || '').trim();
	}

	_formatDate(dateString) {
		if (!dateString) return '';
		const date = new Date(dateString);
		const now = new Date();
		const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

		if (diffDays === 0) return 'Today';
		if (diffDays === 1) return 'Yesterday';
		if (diffDays < 7) return `${diffDays}d ago`;
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}

	_attachEvents() {
		const recentCard = this.element.querySelector('.liveChat-home-recent-card');
		if (recentCard) {
			recentCard.addEventListener('click', () => {
				const convId = recentCard.dataset.conversationId;
				this.state.setActiveConversation(convId);
				this.state.markAsRead(convId);
				this.state.setView('chat');
				if (this.options.onSelectConversation) {
					this.options.onSelectConversation(convId);
				}
			});
		}

		const msgBtn = this.element.querySelector(
			'.liveChat-home-message-btn:not(.liveChat-feedback-btn)'
		);
		if (msgBtn) {
			msgBtn.addEventListener('click', () => {
				this.state.setActiveConversation(null);
				this.state.setView('chat');
			});
		}

		const feedbackBtn = this.element.querySelector('.liveChat-feedback-btn');
		if (feedbackBtn) {
			feedbackBtn.addEventListener('click', () => {
				this.state.setView('feedback');
			});
		}

		this.element
			.querySelectorAll('.liveChat-home-changelog-card')
			.forEach((card) => {
				card.addEventListener('click', () => {
					const item = this.state.homeChangelogItems.find(
						(i) => i.id === card.dataset.changelogId
					);
					if (item?.url) {
						window.open(item.url, '_blank');
					} else {
						this.state.setView('changelog');
					}
				});
			});

		const seeAllBtn = this.element.querySelector(
			'.liveChat-home-changelog-all'
		);
		if (seeAllBtn) {
			seeAllBtn.addEventListener('click', () => {
				this.state.setView('changelog');
			});
		}

		const featuredBtn = this.element.querySelector(
			'.liveChat-home-featured-btn'
		);
		if (featuredBtn) {
			featuredBtn.addEventListener('click', () => {
				const action = featuredBtn.dataset.action;
				const value = featuredBtn.dataset.value;
				if (action === 'url') {
					window.open(value, '_blank');
				} else if (action === 'view') {
					this.state.setView(value);
				}
			});
		}
	}

	destroy() {
		if (this._unsubscribe) {
			this._unsubscribe();
		}
		if (this.element && this.element.parentNode) {
			this.element.parentNode.removeChild(this.element);
		}
	}
}
